import cardAuthClient from './cardAuthClient'

// 新服务登录接口地址。
const LOGIN_ENDPOINT = '/auth/login'
// 新服务退出登录接口地址。
const LOGOUT_ENDPOINT = '/auth/logout'
// 新服务发送注册验证码接口地址。
const REGISTER_VERIFICATION_CODE_ENDPOINT = '/auth/register/verificationCode'
const REGISTER_ENDPOINT = '/auth/register'
// 重新綁定 Google 驗證器前，發送郵箱驗證碼。
const REPLACE_VERIFICATION_CODE_ENDPOINT = '/auth/replace/verificationCode'
// 重新綁定 Google 驗證器前，驗證使用者輸入的郵箱驗證碼。
const REPLACE_VERIFY_EMAIL_CODE_ENDPOINT = '/auth/replace/verifyEmailCod'
// 重新綁定 Google 驗證器時，取得 QRCode、密鑰與 setup_token。
const USER_2FA_SETUP_ENDPOINT = '/auth/2fa/userSetup'
// 重新綁定 Google 驗證器時，驗證 6 位驗證碼並啟用。
const USER_2FA_CONFIRM_ENDPOINT = '/auth/2fa/userConfirm'
// 登录时默认上送的设备标识。
const DEFAULT_DEVICE_NAME = 'vue2-web'

// 统一提取 axios 响应中的业务数据。
function unwrapResponse(response) {
  return response && response.data ? response.data : response
}

// 统一组装登录请求体。
function buildLoginPayload(payload = {}) {
  const requestBody = {
    user_name: payload.userName || payload.user_name || '',
    password: payload.password || '',
    device_name: payload.deviceName || payload.device_name || DEFAULT_DEVICE_NAME,
    fingerprints_md5: payload.fingerprintsMd5 || payload.fingerprints_md5 || '',
    user_cookie: payload.userCookie || payload.user_cookie || ''
  }

  const captchaVerifyParam = payload.captchaVerifyParam || payload.captcha_verify_param
  if (captchaVerifyParam) {
    requestBody.captcha_verify_param = captchaVerifyParam
  }

  const sign = payload.sign
  if (sign) {
    requestBody.sign = sign
  }

  return requestBody
}

// 统一组装注册验证码请求体。
function buildRegisterVerificationCodePayload(payload = {}) {
  return {
    email: payload.email || '',
    captcha_verify_param: payload.captchaVerifyParam || payload.captcha_verify_param || '',
    fingerprints_md5: payload.fingerprintsMd5 || payload.fingerprints_md5 || ''
  }
}

function buildRegisterPayload(payload = {}) {
  return {
    email: payload.email || '',
    password: payload.password || '',
    confirm_password: payload.confirmPassword || payload.confirm_password || '',
    verification_code: payload.verificationCode || payload.verification_code || '',
    invitation_code: payload.invitationCode || payload.invitation_code || '',
    language: payload.language || '',
    fingerprints_md5: payload.fingerprintsMd5 || payload.fingerprints_md5 || ''
  }
}

// 调用新服务登录接口，并在成功后返回最新 cookie 信息。
function login(payload = {}) {
  const requestBody = buildLoginPayload(payload)

  return cardAuthClient.post(LOGIN_ENDPOINT, requestBody, {
    includeCsrf: false
  }).then((response) => {
    const result = unwrapResponse(response)

    return {
      ...result,
      authToken: cardAuthClient.getAuthToken(),
      csrfToken: cardAuthClient.getCsrfToken(),
      cookieHeaderValue: cardAuthClient.buildCookieHeaderValue()
    }
  })
}

// 调用新服务退出接口，并携带签名与 CSRF 信息。
function logout() {
  return cardAuthClient.post(LOGOUT_ENDPOINT, {}, {
    includeCsrf: true
  }).then((response) => {
    const result = unwrapResponse(response)

    return {
      ...result,
      authToken: cardAuthClient.getAuthToken(),
      csrfToken: cardAuthClient.getCsrfToken(),
      cookieHeaderValue: cardAuthClient.buildCookieHeaderValue()
    }
  })
}

// 调用新服务发送注册验证码接口。
function sendRegisterVerificationCode(payload = {}) {
  const requestBody = buildRegisterVerificationCodePayload(payload)

  return cardAuthClient.post(REGISTER_VERIFICATION_CODE_ENDPOINT, requestBody, {
    includeCsrf: false
  }).then(unwrapResponse)
}

function register(payload = {}) {
  const requestBody = buildRegisterPayload(payload)

  return cardAuthClient.post(REGISTER_ENDPOINT, requestBody, {
    includeCsrf: false
  }).then(unwrapResponse)
}

// 呼叫新服務重新綁定 Google 驗證器的發送郵箱驗證碼接口。
function sendReplaceVerificationCode() {
  return cardAuthClient.post(REPLACE_VERIFICATION_CODE_ENDPOINT, {}, {
    includeCsrf: true
  }).then(unwrapResponse)
}

// 呼叫新服務驗證重新綁定流程中的郵箱驗證碼。
function verifyReplaceEmailCode(payload = {}) {
  const requestBody = {
    replace_verification_code: payload.replaceVerificationCode || payload.replace_verification_code || ''
  }

  return cardAuthClient.post(REPLACE_VERIFY_EMAIL_CODE_ENDPOINT, requestBody, {
    includeCsrf: true
  }).then(unwrapResponse)
}

// 呼叫新服務取得重新綁定 Google 驗證器所需的 QRCode、密鑰與 setup_token。
function setupUser2fa() {
  return cardAuthClient.post(USER_2FA_SETUP_ENDPOINT, {}, {
    includeCsrf: true
  }).then(unwrapResponse)
}

// 呼叫新服務驗證 Google 驗證器 6 位碼，驗證成功後完成重新綁定。
function confirmUser2fa(payload = {}) {
  const requestBody = {
    setup_token: payload.setupToken || payload.setup_token || '',
    code: payload.code || ''
  }

  return cardAuthClient.post(USER_2FA_CONFIRM_ENDPOINT, requestBody, {
    includeCsrf: true
  }).then(unwrapResponse)
}

// 获取浏览器中当前保存的新服务认证 cookie。
function getAuthToken() {
  return cardAuthClient.getAuthToken()
}

// 获取浏览器中当前保存的新服务 CSRF cookie。
function getCsrfToken() {
  return cardAuthClient.getCsrfToken()
}

// 获取可供旧接口桥接使用的 Cookie 头字符串。
function getCookieHeaderValue() {
  return cardAuthClient.buildCookieHeaderValue()
}

// 判断当前浏览器中是否已经存在新服务登录态。
function hasAuthToken() {
  return !!getAuthToken()
}

const cardAuthService = {
  login,
  logout,
  register,
  sendRegisterVerificationCode,
  sendReplaceVerificationCode,
  verifyReplaceEmailCode,
  setupUser2fa,
  confirmUser2fa,
  getAuthToken,
  getCsrfToken,
  getCookieHeaderValue,
  hasAuthToken
}

export {
  DEFAULT_DEVICE_NAME,
  LOGIN_ENDPOINT,
  LOGOUT_ENDPOINT,
  REGISTER_ENDPOINT,
  REGISTER_VERIFICATION_CODE_ENDPOINT,
  REPLACE_VERIFICATION_CODE_ENDPOINT,
  REPLACE_VERIFY_EMAIL_CODE_ENDPOINT,
  USER_2FA_SETUP_ENDPOINT,
  USER_2FA_CONFIRM_ENDPOINT,
  buildLoginPayload,
  buildRegisterPayload,
  buildRegisterVerificationCodePayload,
  confirmUser2fa,
  getAuthToken,
  getCookieHeaderValue,
  getCsrfToken,
  hasAuthToken,
  login,
  logout,
  register,
  sendRegisterVerificationCode,
  sendReplaceVerificationCode,
  setupUser2fa,
  verifyReplaceEmailCode,
  unwrapResponse
}

export default cardAuthService
